/**
 * `optionsUrl` shorthand resolution — turns declarative option-endpoint URLs
 * on field/filter configs into fully-wired `loadOptions` (+ `resolveLabel` /
 * `lookupOptions` where the type supports exact-value hydration), with strict
 * misconfiguration guards. Single owner of optionsUrl validation semantics.
 */

import { createApiOptionsLoader } from '../../core/options/api-options-loader.js';
import { createApiOptionsLookup } from '../../core/options/api-options-lookup.js';
import { createBatchedResolveLabel } from '../../core/options/batched-resolve-label.js';

/** Field types that support the `optionsUrl` shorthand for wiring `loadOptions`. */
const OPTIONSURL_TYPES = new Set(['async-select', 'multi-relation', 'checkbox-group', 'form-link']);

/**
 * Validates and normalizes an optionsUrl value.
 * Strips trailing slashes, collapses leading duplicate slashes to one.
 * Returns the normalized URL string.
 *
 * @param {unknown} url
 * @param {string} fieldKey
 * @param {string} [kind] - 'field' or 'filter'. Used in error messages. Default: 'field'.
 * @returns {string} normalized URL
 */
function validateOptionsUrl(url, fieldKey, kind = 'field') {
  if (typeof url !== 'string') {
    throw new Error(`${kind} '${fieldKey}': optionsUrl must be a string`);
  }
  const trimmed = url.trim();
  if (!trimmed) {
    throw new Error(`${kind} '${fieldKey}': optionsUrl must be a non-empty path`);
  }
  // Reject dangerous chars on raw input (before normalization) for clear error messages
  if (/\s/.test(trimmed)) {
    throw new Error(`${kind} '${fieldKey}': optionsUrl must not contain whitespace`);
  }
  if (trimmed.includes('?') || trimmed.includes('#')) {
    throw new Error(`${kind} '${fieldKey}': optionsUrl must not contain query or fragment`);
  }
  if (trimmed.includes('\\')) {
    throw new Error(`${kind} '${fieldKey}': optionsUrl must not contain backslashes`);
  }
  // Normalize: collapse leading slashes to one, strip trailing slashes
  const normalized = '/' + trimmed.replace(/^\/+/, '').replace(/\/+$/, '');
  if (normalized === '/') {
    throw new Error(`${kind} '${fieldKey}': optionsUrl must be a non-empty path`);
  }
  if (normalized.startsWith('/api/') || normalized === '/api') {
    throw new Error(`${kind} '${fieldKey}': optionsUrl must not include /api prefix (apiClient prepends baseUrl)`);
  }
  return normalized;
}

/**
 * Resolves fields that use the `optionsUrl` shorthand into fully-wired
 * field definitions with `loadOptions` (and `resolveLabel` for the
 * lookup-capable types: async-select, multi-relation, form-link).
 *
 * Supported types: async-select, multi-relation, checkbox-group, form-link.
 *
 * @param {import('../../contracts/forms.js').FieldDef[]} fields
 * @param {import('../../contracts/api.js').ApiClient} apiClient
 * @param {{
 *   logger?: import('../../contracts/page.js').PageServices['logger'],
 *   signal?: AbortSignal,
 * }} [deps] Page-scoped logger + abort signal. Forwarded to the lookup helper
 *   (logger) and the batched resolveLabel (signal).
 * @returns {import('../../contracts/forms.js').FieldDef[]}
 */
export function resolveOptionsUrlFields(fields, apiClient, deps) {
  const { logger, signal } = deps ?? {};
  return fields.map(f => {
    if (!OPTIONSURL_TYPES.has(f.type)) return f;
    const raw = /** @type {Record<string, unknown>} */ (f);

    // Strict type guards — misconfigs caught early (undefined is allowed — common with spread configs)
    if (raw.loadOptions != null && typeof raw.loadOptions !== 'function') {
      throw new Error(`field '${f.key}': loadOptions must be a function`);
    }
    if (raw.resolveLabel != null && typeof raw.resolveLabel !== 'function') {
      throw new Error(`field '${f.key}': resolveLabel must be a function`);
    }

    // Already has loadOptions — pass through unchanged
    if (typeof raw.loadOptions === 'function') return f;

    // Needs optionsUrl (checkbox-group can use static options array instead)
    if (!('optionsUrl' in raw)) {
      if (f.type === 'checkbox-group') return f;
      throw new Error(`field '${f.key}' requires either loadOptions or optionsUrl`);
    }
    const normalizedUrl = validateOptionsUrl(raw.optionsUrl, f.key);

    // --- Derive valuesParams ---
    // Explicit valuesParams on the field def wins.
    // Otherwise, auto-derive from dependsOn (each key maps 1:1 to a query param name).
    /** @type {Record<string, string> | undefined} */
    let valuesParams;
    if (raw.valuesParams != null) {
      if (typeof raw.valuesParams !== 'object' || Array.isArray(raw.valuesParams)) {
        throw new Error(`field '${f.key}': valuesParams must be an object (Record<string, string>)`);
      }
      for (const [k, v] of Object.entries(raw.valuesParams)) {
        if (k.trim() === '') {
          throw new Error(`field '${f.key}': valuesParams keys must be non-empty strings`);
        }
        if (typeof v !== 'string' || v.trim() === '') {
          throw new Error(`field '${f.key}': valuesParams['${k}'] must be a non-empty string`);
        }
      }
      /** @type {Record<string, string>} */
      const normalized = {};
      for (const [k, v] of Object.entries(raw.valuesParams)) {
        normalized[k.trim()] = v.trim();
      }
      valuesParams = normalized;
    } else if (Array.isArray(raw.dependsOn) && raw.dependsOn.length > 0) {
      valuesParams = {};
      for (const depKey of raw.dependsOn) {
        if (typeof depKey !== 'string') {
          throw new Error(`field '${f.key}': dependsOn entries must be strings, got ${typeof depKey}`);
        }
        const dep = depKey.trim();
        if (dep === '') {
          throw new Error(`field '${f.key}': dependsOn entries must be non-empty strings`);
        }
        valuesParams[dep] = dep;
      }
    }

    // For multi-relation, valueProp means "wire object FK column" (for codec),
    // not "options response value property". The options endpoint returns
    // standard { value, label }, so the loader must use the default 'value'.
    const loaderValueProp = f.type === 'multi-relation'
      ? undefined
      : (typeof raw.valueProp === 'string' ? raw.valueProp : undefined);

    // Cap search results at 50 for editor field dropdowns. Selected values
    // outside the visible page are still resolved via the batched lookup
    // (see resolveLabel below), so capping is safe here.
    const isLookupCapable = f.type === 'async-select' || f.type === 'multi-relation' || f.type === 'form-link';
    const loadOptions = createApiOptionsLoader(apiClient, {
      url: normalizedUrl,
      valueProp: loaderValueProp,
      labelProp: typeof raw.labelProp === 'string' ? raw.labelProp : undefined,
      iconProp: typeof raw.iconProp === 'string' ? raw.iconProp : undefined,
      iconAs: typeof raw.iconAs === 'string' ? raw.iconAs : undefined,
      queryParam: typeof raw.queryParam === 'string' ? raw.queryParam : undefined,
      valuesParams,
      limit: isLookupCapable ? 50 : undefined,
    });

    // Strip factory-only props, inject loadOptions.
    // Preserve valueProp for multi-relation codec injection downstream.
    const { optionsUrl: _optionsUrl, valueProp: vp, labelProp: _labelProp, iconProp: _iconProp, iconAs: _iconAs, queryParam: _queryParam, valuesParams: _vp, ...rest } = raw;
    const result = /** @type {Record<string, unknown>} */ ({ ...rest, loadOptions });
    if (f.type === 'multi-relation' && typeof vp === 'string') {
      result.valueProp = vp;
    }

    // Auto-generate resolveLabel for async-select and multi-relation.
    // Backed by exact-value lookup (`/options?values=...`) — bypasses the
    // search cap — and microtask-coalesced so a single editor open issues at
    // most one batched request per field. Cache lifetime is the page-factory
    // closure, so close+reopen reads cached labels.
    // Explicit resolveLabel from page config wins.
    if (isLookupCapable) {
      if (typeof raw.resolveLabel === 'function') {
        result.resolveLabel = raw.resolveLabel;
      } else {
        const lookupOptions = createApiOptionsLookup(apiClient, {
          url: normalizedUrl,
          valueProp: loaderValueProp,
          labelProp: typeof raw.labelProp === 'string' ? raw.labelProp : undefined,
          iconProp: typeof raw.iconProp === 'string' ? raw.iconProp : undefined,
          iconAs: typeof raw.iconAs === 'string' ? raw.iconAs : undefined,
        }, { logger });
        result.resolveLabel = createBatchedResolveLabel(lookupOptions, { signal });
      }
    }

    return /** @type {import('../../contracts/forms.js').FieldDef} */ (result);
  });
}

/** Filter types that support optionsUrl resolution. */
const FILTER_OPTIONSURL_TYPES = new Set(['select', 'multiselect', 'radio']);

/**
 * Resolves filters that use `optionsUrl` into fully-wired FilterDefs with
 * `loadOptions`. For `multiselect` filters, also injects an exact-value
 * `lookupOptions` so the async multiselect driver can hydrate selected-chip
 * labels for values outside the current search page.
 *
 * `select`/`radio` filters intentionally do NOT receive `limit` or
 * `lookupOptions`: those drivers clear `currentValue` when it falls outside
 * loaded options (see `selectFilterDriver` / `radioFilterDriver`), so capping
 * them silently breaks deep links until they have search UI.
 *
 * @param {import('../../contracts/filters.js').FilterDef[]} filters
 * @param {import('../../contracts/api.js').ApiClient} apiClient
 * @param {{
 *   logger?: import('../../contracts/page.js').PageServices['logger'],
 *   signal?: AbortSignal,
 * }} [deps] Page-scoped logger + abort signal. Logger is forwarded to the
 *   lookup helper. Signal is currently reserved for future use.
 * @returns {import('../../contracts/filters.js').FilterDef[]}
 */
export function resolveOptionsUrlFilters(filters, apiClient, deps) {
  const { logger } = deps ?? {};
  return filters.map(f => {
    if (!FILTER_OPTIONSURL_TYPES.has(f.type)) return f;
    const raw = /** @type {Record<string, unknown>} */ (f);

    // Strict type guard — matches field-side behavior (undefined is allowed — common with spread configs)
    if (raw.loadOptions != null && typeof raw.loadOptions !== 'function') {
      throw new Error(`filter '${f.key}': loadOptions must be a function`);
    }
    if (raw.lookupOptions != null && typeof raw.lookupOptions !== 'function') {
      throw new Error(`filter '${f.key}': lookupOptions must be a function`);
    }

    // Explicit loadOptions (function) takes precedence
    if (typeof raw.loadOptions === 'function') return f;

    if (!('optionsUrl' in raw)) return f;

    const normalizedUrl = validateOptionsUrl(raw.optionsUrl, f.key, 'filter');
    const isLookupCapable = f.type === 'multiselect';
    const loadOptions = createApiOptionsLoader(apiClient, {
      url: normalizedUrl,
      valueProp: typeof raw.valueProp === 'string' ? raw.valueProp : undefined,
      labelProp: typeof raw.labelProp === 'string' ? raw.labelProp : undefined,
      iconProp: typeof raw.iconProp === 'string' ? raw.iconProp : undefined,
      iconAs: typeof raw.iconAs === 'string' ? raw.iconAs : undefined,
      queryParam: typeof raw.queryParam === 'string' ? raw.queryParam : undefined,
      // Cap multiselect filter searches at 50 (safe because the async
      // multiselect driver hydrates selected labels via lookupOptions).
      // select/radio filters stay uncapped until they have search UI.
      limit: isLookupCapable ? 50 : undefined,
    });

    // Strip factory-only props, but KEEP static options (hybrid pattern)
    const { optionsUrl: _optionsUrl, valueProp: _valueProp, labelProp: _labelProp, iconProp: _iconProp, iconAs: _iconAs, queryParam: _queryParam, lookupOptions: _lookupOptions, ...rest } = raw;
    /** @type {Record<string, unknown>} */
    const result = { ...rest, loadOptions };

    if (isLookupCapable) {
      // Explicit user-supplied lookupOptions wins (Liskov).
      if (typeof raw.lookupOptions === 'function') {
        result.lookupOptions = raw.lookupOptions;
      } else {
        result.lookupOptions = createApiOptionsLookup(apiClient, {
          url: normalizedUrl,
          valueProp: typeof raw.valueProp === 'string' ? raw.valueProp : undefined,
          labelProp: typeof raw.labelProp === 'string' ? raw.labelProp : undefined,
          iconProp: typeof raw.iconProp === 'string' ? raw.iconProp : undefined,
          iconAs: typeof raw.iconAs === 'string' ? raw.iconAs : undefined,
        }, { logger });
      }
    }

    return /** @type {import('../../contracts/filters.js').FilterDef} */ (result);
  });
}
